import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  model,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { ImageComponent } from '@shared/components/image';
import { PregenerationSize } from '@shared/components/image/models';
import { getCDNLink } from '@shared/components/image/utils';
import { SvgComponent } from '@shared/components/svg';
import { Video } from '@shared/components/video/models';
import { VideoService } from '@shared/components/video/services';
import { ViewportObserverDirective } from '@shared/directives';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { getStableId } from '@shared/utils';

/**
 * A reactive video player component that manages playback via a centralized `VideoService`.
 *
 * This component handles video rendering, autoplay on viewport entry, and playback exclusivity
 * based on groups. It uses Angular Signals to reactively update its state in response to
 * user interactions and changes in the global video queue.
 *
 * @param video - The required `Video` object containing URLs and metadata.
 * @param keepAspectRatio - When `true`, maintains the video's aspect ratio. Defaults to `false`.
 * @param autoplay - When `true`, the video attempts to play automatically when it enters the viewport. Defaults to `false`.
 * @param muted - Controls the video's muted state. Can be updated externally. Defaults to `false`.
 * @param showPlayButton - Toggles the visibility of the custom play/pause button. Defaults to `true`.
 * @param loop - When `true`, the video will loop. `autoplay` also enables this. Defaults to `false`.
 * @param showPoster - Two-way binding (`model`) to control the poster image visibility. Automatically set to `false` if no poster is provided.
 * @param showControls - Toggles the native browser video controls. Defaults to `false`.
 * @param groupName - Optional group identifier. If provided, only one video from this group can play at a time across the application.
 *
 * @example
 * ```
 * <!-- Basic usage -->
 * <app-video [video]="myVideo" />
 *
 * <!-- Autoplay video within a group -->
 * <app-video
 *   [video]="myTrailerVideo"
 *   [autoplay]="true"
 *   [groupName]="'product-trailers'"
 * />
 * ```
 *
 * @remarks
 * - **Playback Coordination**: This component does not play/pause directly. Instead, it adds/removes its unique ID from the `VideoService`. An `effect` listens to this service and triggers playback accordingly.
 * - **Group Exclusivity**: Using `groupName` ensures that when this video starts, any other video in the same group is automatically stopped.
 * - **Autoplay Behavior**: `autoplay` is tied to `ViewportObserverDirective`. The video is added to the play queue when it becomes visible and removed when it leaves. Autoplay also forces `muted` and `loop` to `true`.
 * - **Error Handling**: If the browser blocks autoplay (`NotAllowedError`), the component displays a play button for the user to initiate playback manually.
 * - **State Management**: Internal states like loading, playing, or error are managed by signals, ensuring `OnPush` compatibility.
 */
@Component({
  selector: 'app-video',
  imports: [ImageComponent, SvgComponent, ViewportObserverDirective],
  templateUrl: './video.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoComponent implements OnInit, OnDestroy {
  readonly video = input.required<Video>();
  readonly keepAspectRatio = input(false);
  readonly autoplay = input(false);
  readonly muted = input(false);
  readonly showPlayButton = input(true);
  readonly loop = input(false);
  readonly showPoster = model(true);
  readonly showControls = input(false);
  readonly groupName = input<string>();
  readonly isImagePriority = input(false);
  /** The poster's frame for the server pass, as `app-image` takes it. */
  readonly posterPregenerationSize = input<PregenerationSize>();

  protected readonly isLoading = signal(true);
  protected readonly videoId = computed(() => {
    const url = this.video().url ?? '';
    return getStableId(url, this.groupName());
  });
  protected readonly videoElement = viewChild.required<ElementRef<HTMLVideoElement>>('player');
  protected readonly videoUrl = computed(() => getCDNLink(this.video().url));
  protected readonly isStart = signal(false);
  protected readonly isCanPlay = signal(false);
  protected readonly isPlayError = signal(false);
  /** Indicates if the video source was cleared to release GPU resources */
  protected readonly isLobotomized = signal(false);
  /** Effective video URL (empty when lobotomized) */
  protected readonly effectiveSrc = computed(() => (this.isLobotomized() ? '' : (this.videoUrl() ?? '')));
  protected readonly isMuted = linkedSignal(() => this.muted());
  protected readonly isLoop = linkedSignal(() => this.loop());
  private readonly isBrowser = inject(PlatformContextService).isBrowser;
  private readonly videoService = inject(VideoService);
  private readonly playingVideos = this.videoService.playingVideos;
  private readonly logs = inject(LogsService);
  private player!: HTMLVideoElement;

  constructor() {
    afterNextRender(() => {
      if (!this.isBrowser()) return;
      this.getElement();
    });

    effect(() => {
      const isCanPlay = this.isCanPlay();
      const playingVideos = this.playingVideos();

      if (!isCanPlay) return;

      const id = untracked(this.videoId);
      const inQueue = playingVideos.has(id);
      const isCurrentlyPlaying = untracked(this.isStart);

      this.logs.trace('VideoComponent', `Effect triggered`, { id, inQueue, isCurrentlyPlaying });

      if (!inQueue && isCurrentlyPlaying) {
        this.stop();
      } else if (inQueue && !isCurrentlyPlaying) {
        this.play();
      }
    });
  }

  ngOnInit(): void {
    const poster = this.video()?.poster;
    if (!poster) {
      this.showPoster.set(false);
    }
  }

  ngOnDestroy(): void {
    this.videoService.removeVideoFromQueue(this.videoId());
  }

  /**
   * Handles the media `canplay` readiness by applying playback constraints and updating readiness flags.
   *
   * @remarks
   * - In browsers, disables Picture-in-Picture and remote playback on the video element.
   * - If `autoplay` is enabled, forces `muted` and `loop` to `true` to comply with autoplay policies.
   * - Marks loading finished and signals that the media can play.
   */
  handleCanPlay(): void {
    this.logs.trace('VideoComponent', `canplay event received`, { id: this.videoId() });

    if (this.isBrowser()) {
      this.player.disablePictureInPicture = true;
      this.player.disableRemotePlayback = true;
    }

    if (this.autoplay()) {
      this.isMuted.set(true);
      this.isLoop.set(true);
    }
    this.isLoading.set(false);
    this.isCanPlay.set(true);
  }

  /**
   * Handles explicit play intent by enqueuing this video ID in the global `VideoService` queue.
   *
   * @remarks
   * - Clears any previous play error indicator.
   * - Queue membership is used by the internal `effect` to actually start playback.
   */
  handleOnPlay(): void {
    this.logs.debug('VideoComponent', `Manual play requested`, { id: this.videoId() });
    this.isPlayError.set(false);
    const id = this.videoId();
    this.videoService.addVideoToQueue(id);
  }

  /**
   * Reacts to viewport visibility to coordinate autoplay with the global queue.
   *
   * @param inViewport - `true` when the player is visible in the viewport.
   *
   * @remarks
   * - When `autoplay` is enabled: adds the video to the queue on entry, removes it on exit.
   * - When `autoplay` is disabled: always removes the video from the queue to avoid unintended playback.
   * - Clears any play error indicator when removing from the queue.
   */
  handleViewportObserver(inViewport: boolean): void {
    const id = this.videoId();
    this.logs.trace('VideoComponent', `Viewport changed`, { id, inViewport, isLobotomized: this.isLobotomized() });

    if (inViewport) {
      // Restore source if it was lobotomized
      if (this.isLobotomized()) {
        this.logs.debug('VideoComponent', `Restoring lobotomized video`, { id });
        this.isLobotomized.set(false);
        // isCanPlay will be set back to true by handleCanPlay after reload
      }

      if (this.autoplay()) {
        this.videoService.addVideoToQueue(id);
      }
    } else {
      // Exiting viewport
      this.isPlayError.set(false);
      this.videoService.removeVideoFromQueue(id);

      // Lobotomize: clear source to release GPU resources
      this.lobotomize();
    }
  }

  /**
   * Attempts to start playback of the underlying `<video>` element.
   *
   * @private
   * @remarks
   * - No-ops on the server or if the element is not yet available.
   * - Sets a "started" flag and calls `HTMLMediaElement.play()`.
   * - Logs errors; if blocked by autoplay policies (`NotAllowedError`), sets a flag to show a manual Play button.
   */
  private play(): void {
    if (!this.isBrowser()) return;
    if (!this.player) return;

    const id = this.videoId();
    this.logs.info('VideoComponent', `Starting playback`, { id, url: this.videoUrl() });

    this.isStart.set(true);
    void this.player.play().catch((error: unknown) => {
      if (error instanceof Error) {
        this.logs.warn('VideoComponent', `Video could not be played: \nurl: ${this.videoUrl()} \nid: ${id}\n`, error);
        if (error.name === 'NotAllowedError') {
          this.logs.info('VideoComponent', 'Playback blocked, showing Play button.');
          this.isPlayError.set(true);
        }
      } else {
        this.logs.warn('VideoComponent', `Unknown error for video ${this.videoUrl()}`, error);
      }
    });
  }

  /**
   * Pauses the underlying `<video>` element and clears the "started" flag.
   *
   * @private
   * @remarks
   * - No-ops on the server.
   */
  private stop(): void {
    if (this.isBrowser()) {
      this.logs.info('VideoComponent', `Stopping playback`, { id: this.videoId() });
      this.player.pause();
    }
    this.isStart.set(false);
  }

  /**
   * Lobotomizes the video: clears the src and calls load() to release GPU resources.
   *
   * @private
   * @remarks
   * - The browser immediately releases hardware resources (D3D11)
   * - The <video> element stays in the DOM, ready to be reactivated
   */
  private lobotomize(): void {
    if (!this.isBrowser()) return;

    const id = this.videoId();
    this.logs.info('VideoComponent', `Lobotomizing video (releasing GPU resources)`, { id });

    this.player.pause();
    this.isLobotomized.set(true);
    this.isCanPlay.set(false);
    this.isStart.set(false);

    // Force the browser to release GPU resources
    // The [src]="effectiveSrc()" binding will set src='' via Angular
    // We call load() in the next tick to apply the change
    setTimeout(() => {
      if (this.player && this.isLobotomized()) {
        this.player.load();
        this.logs.trace('VideoComponent', `GPU resources released`, { id });
      }
    }, 0);
  }

  /**
   * Caches the native `<video>` element from the `viewChild` query after the first render.
   *
   * @private
   * @remarks
   * - No-ops on the server.
   */
  private getElement(): void {
    if (!this.isBrowser()) return;
    this.player = this.videoElement().nativeElement;
  }
}
